import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Image, Plus, Trash2, X, Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";

interface ProjectFormProps {
  project?: any;
  isEditing?: boolean;
  onSubmit?: (project: ProjectData) => void;
}

interface ProjectData {
  title: string;
  description: string;
  category: string;
  tags: string[];
  coverImage: string | File;
  coverImagePreview?: string;
  projectUrl?: string;
  githubUrl?: string;
  published?: boolean;
}

const ProjectForm = ({ project, isEditing = false, onSubmit }: ProjectFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTag, setNewTag] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [projectData, setProjectData] = useState<ProjectData>({
    title: "",
    description: "",
    category: "",
    tags: [],
    coverImage: "",
    projectUrl: "",
    githubUrl: "",
    published: false,
    coverImagePreview: "",
  });

  useEffect(() => {
    if (project && isEditing) {
      setProjectData({
        title: project.title || "",
        description: project.description || "",
        category: project.category || "",
        tags: project.technologies || [],
        coverImage: project.image_url || "",
        projectUrl: project.project_url || "",
        githubUrl: project.github_url || "",
        published: project.published || false,
      });
    }
  }, [project, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value: string) => {
    setProjectData((prev) => ({ ...prev, category: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !projectData.tags.includes(newTag.trim())) {
      setProjectData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setProjectData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handlePublishChange = (checked: boolean) => {
    setProjectData(prev => ({ 
      ...prev, 
      published: checked 
    }));
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    try {
      console.log("Starting image upload:", file.name, file.size);
      
      // Validate file size (limit to 2MB for better upload success)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error("File size too large. Please upload an image smaller than 2MB.");
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${path}/${fileName}`;
      
      console.log("Uploading to path:", filePath);

      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("You must be logged in to upload images.");
      }

      const { error: uploadError, data } = await supabase.storage
        .from('project-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error("Supabase upload error:", uploadError);
        throw uploadError;
      }

      console.log("Upload successful, getting public URL");
      const { data: publicUrlData } = supabase.storage
        .from('project-images')
        .getPublicUrl(filePath);

      console.log("Public URL acquired:", publicUrlData.publicUrl);
      return publicUrlData.publicUrl;
    } catch (error: any) {
      console.error("Error uploading image:", error.message);
      throw new Error(`Error uploading image: ${error.message}`);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      try {
        setProjectData((prev) => ({ ...prev, coverImage: file }));
        const imageUrl = URL.createObjectURL(file);
        setProjectData((prev) => ({ ...prev, coverImagePreview: imageUrl }));
        console.log("Image selected:", file.name, "Preview URL created");
      } catch (error: any) {
        console.error("Error handling image:", error);
        toast({
          title: "Image Upload Failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } else {
      console.log("No file selected in the input");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!projectData.title || !projectData.description || !projectData.category) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to create a project.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let coverImageUrl = typeof projectData.coverImage === 'string' 
        ? projectData.coverImage 
        : "";
      
      if (projectData.coverImage instanceof File) {
        coverImageUrl = await uploadImage(projectData.coverImage, 'covers');
        console.log("Uploaded cover image:", coverImageUrl);
      }
      
      const projectPayload = {
        user_id: user.id,
        title: projectData.title,
        description: projectData.description,
        image_url: coverImageUrl,
        project_url: projectData.projectUrl,
        github_url: projectData.githubUrl,
        technologies: projectData.tags,
        category: projectData.category,
        published: projectData.published,
      };

      let response;
      
      if (isEditing && project?.id) {
        response = await supabase
          .from('projects')
          .update(projectPayload)
          .eq('id', project.id)
          .select();
          
        toast({
          title: "Project updated",
          description: "Your project has been successfully updated.",
        });
      } else {
        response = await supabase
          .from('projects')
          .insert(projectPayload)
          .select();
          
        toast({
          title: "Project created",
          description: "Your project has been successfully created.",
        });
      }
      
      if (response.error) {
        throw response.error;
      }
      
      if (onSubmit) {
        onSubmit(projectData);
      }
      
      navigate('/admin');
    } catch (error: any) {
      console.error('Error saving project:', error.message);
      toast({
        title: "Failed to save project",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValidUrl = (urlString: string) => {
    try {
      return Boolean(new URL(urlString));
    } catch (e) {
      return false;
    }
  };

  const getCoverImagePreview = () => {
    if (projectData.coverImage instanceof File && projectData.coverImagePreview) {
      return projectData.coverImagePreview;
    } else if (typeof projectData.coverImage === 'string' && projectData.coverImage) {
      return projectData.coverImage;
    }
    return null;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-brand-blue">
          {isEditing ? "Edit Project" : "Create a New Project"}
        </CardTitle>
        <CardDescription>
          {isEditing 
            ? "Update your project details below." 
            : "Showcase your work by adding a new project to your portfolio."}
        </CardDescription>
      </CardHeader>
      
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium">
              Project Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              name="title"
              value={projectData.title}
              onChange={handleChange}
              placeholder="Enter project title"
              className="w-full"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Project Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              name="description"
              value={projectData.description}
              onChange={handleChange}
              placeholder="Describe your project, including challenges and solutions"
              className="min-h-[150px] w-full"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="category" className="text-sm font-medium">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={projectData.category}
              onValueChange={handleCategoryChange}
              required
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="web-development">Web Development</SelectItem>
                <SelectItem value="mobile-app">Mobile App</SelectItem>
                <SelectItem value="ui-design">UI/UX Design</SelectItem>
                <SelectItem value="product-management">Product Management</SelectItem>
                <SelectItem value="e-commerce">E-commerce</SelectItem>
                <SelectItem value="saas">SaaS</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="projectUrl" className="text-sm font-medium">
                Project URL
              </Label>
              <Input
                id="projectUrl"
                name="projectUrl"
                value={projectData.projectUrl}
                onChange={handleChange}
                placeholder="https://your-project.com"
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="githubUrl" className="text-sm font-medium">
                GitHub URL
              </Label>
              <Input
                id="githubUrl"
                name="githubUrl"
                value={projectData.githubUrl}
                onChange={handleChange}
                placeholder="https://github.com/username/repo"
                className="w-full"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {projectData.tags.map((tag) => (
                <div 
                  key={tag} 
                  className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full"
                >
                  <span className="text-sm">{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add a tag"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Cover Image (Main project image)
            </Label>
            <div className="grid gap-4">
              {getCoverImagePreview() ? (
                <div className="relative rounded-lg overflow-hidden aspect-video bg-gray-100">
                  <img 
                    src={getCoverImagePreview()} 
                    alt="Cover Preview" 
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={() => setProjectData((prev) => ({ 
                      ...prev, 
                      coverImage: "",
                      coverImagePreview: "" 
                    }))}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <div className="mb-4">
                    <Image size={40} className="mx-auto text-gray-400" />
                  </div>
                  <div className="text-gray-500 mb-4">
                    <p className="text-sm">Upload a cover image</p>
                    <p className="text-xs text-gray-400">PNG, JPG, GIF up to 5MB</p>
                  </div>
                  <Button 
                    variant="outline" 
                    type="button" 
                    className="flex items-center gap-2"
                    onClick={triggerFileInput}
                  >
                    <Upload size={16} /> Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleImageUpload}
                    accept="image/*"
                    id="coverImage"
                    name="coverImage"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch 
              id="published"
              checked={projectData.published}
              onCheckedChange={handlePublishChange}
            />
            <Label htmlFor="published" className="text-sm font-medium">
              {projectData.published ? (
                <span className="flex items-center gap-1 text-green-600">
                  <Check size={16} /> Published
                </span>
              ) : (
                "Save as Draft"
              )}
            </Label>
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between border-t pt-6">
          <Button 
            variant="outline" 
            type="button"
            onClick={() => navigate('/admin')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : isEditing ? "Update Project" : "Create Project"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default ProjectForm;
